import React, {useEffect, useRef, useState} from 'react';
import ButtonSolid from 'components/Buttons/ButtonSolid';
import {createPatientRecord} from 'externalAPIs/insurance-eligibility';
import {SurveyData} from 'screens/InsomniaSurvey/utils/surveyData';
import {getEmailFromSurveyData} from 'utils/getEmailFromSurveyData';
import {getPayerIdByValue} from 'surveys/insomnia_funnel/paywall/utils';
import Analytics from 'utils/Analytics';
import styles from './CareTeamHandoffInterstitial.module.css';

export const CARE_TEAM_EMAIL = 'care@risescience.com';

/**
 * Terminal screen for patients who finished the booking intake with no bookable
 * slot. Two jobs, in this order:
 *
 *  1. Persist them — `healthie/create-patient` saves the record and creates the
 *     Healthie chart, so the care team schedules from a complete intake instead
 *     of re-collecting it over email.
 *  2. Hand them off — a mailto: to the care team, framed as the next step.
 *
 * The mailto is offered whether or not step 1 succeeded: a patient who reaches
 * this screen should never be left without a way to reach someone. The save
 * runs once (guarded), and a failure is reported to the care team through the
 * copy rather than as an error the patient has to act on.
 *
 * This page does not complete the survey — there is no appointment to check out
 * with, so `insomnia_booking` leaves it as the end of the road.
 */
const CareTeamHandoffInterstitial: React.FC = () => {
  const [isSaving, setIsSaving] = useState(true);
  const [saved, setSaved] = useState(false);
  const hasSubmitted = useRef(false);

  const surveyData = SurveyData.read() ?? {};
  const firstName = surveyData.first_name ?? '';
  const email = getEmailFromSurveyData() ?? '';
  const state = surveyData.insurance_state ?? '';
  const insurance = surveyData.insurance_coverage ?? '';

  useEffect(() => {
    // React 18 StrictMode runs effects twice in development; the endpoint is
    // idempotent but there is no reason to call it twice.
    if (hasSubmitted.current) {
      return;
    }
    hasSubmitted.current = true;

    const personId = surveyData.person_id;
    const token = surveyData.eligibility_token;

    if (!personId || !token) {
      // Reachable from the QA preview route, which renders this screen without
      // an account. Nothing to save — still show the hand-off.
      console.warn('CareTeamHandoff: missing account information, skipping patient creation');
      setIsSaving(false);
      return;
    }

    createPatientRecord(personId, firstName, surveyData.last_name ?? '', email, surveyData.phone ?? '', token, {
      state,
      dateOfBirth: surveyData.dob,
      gender: surveyData.gender?.toLowerCase(),
      memberId: surveyData.member_id,
      payerName: getPayerIdByValue(insurance),
      ref: surveyData.referral_code,
    })
      .then(result => {
        setSaved(true);
        Analytics.track('Clinic Booking Care Team Handoff Saved', {
          healthie_patient_created: Boolean(result?.healthiePatientId),
          healthie_error: result?.healthieError ?? undefined,
          insurance_state: state,
          insurance_coverage: insurance,
        });
      })
      .catch(error => {
        console.error('CareTeamHandoff: error creating patient record:', error);
        Analytics.track('Clinic Booking Care Team Handoff Save Failed', {
          error_message: error?.message,
          insurance_state: state,
          insurance_coverage: insurance,
        });
      })
      .finally(() => {
        setIsSaving(false);
      });
    // Intentionally runs once on mount.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleEmailCareTeam = () => {
    Analytics.track('Clinic Booking Care Team Email Clicked', {
      record_saved: saved,
      insurance_state: state,
      insurance_coverage: insurance,
    });

    // Keep the prefilled body light on personal detail — the care team looks the
    // patient up from the record we just saved.
    const subject = 'Scheduling my sleep session';
    const body = [
      `Hi Rise care team,`,
      ``,
      `I finished the online intake but there were no session times available. Could you help me find one?`,
      ``,
      firstName ? `Name: ${firstName} ${surveyData.last_name ?? ''}`.trim() : '',
      email ? `Email: ${email}` : '',
      state ? `State: ${state}` : '',
    ]
      .filter(Boolean)
      .join('\n');

    window.location.href = `mailto:${CARE_TEAM_EMAIL}?subject=${encodeURIComponent(
      subject,
    )}&body=${encodeURIComponent(body)}`;
  };

  return (
    <div className={`${styles.container} ${styles.centered}`}>
      <div className={styles.heroCard}>
        <div className={styles.kicker}>Next step</div>
        <h1 className={styles.title}>
          {firstName ? `${firstName}, our care team takes it from here` : 'Our care team takes it from here'}
        </h1>
        <p className={styles.body}>
          {isSaving
            ? 'Saving your details…'
            : `Your intake is with our clinical team. They'll reach out${
                email ? ` at ${email}` : ''
              } to find a session time that fits — usually within one business day.`}
        </p>
        <ul className={styles.bulletList}>
          <li className={styles.bullet}>Nothing else to fill out — we have your insurance and health details.</li>
          <li className={styles.bullet}>You'll get session options by email, and can pick what works.</li>
          <li className={styles.bullet}>Most eligible patients pay $0 for their first session.</li>
        </ul>
      </div>
      <div className={styles.footer}>
        {/* Never gated on the save: reaching a human is the point of this
            screen, and the save is a background convenience for the care team.
            A patient who taps immediately still gets the mailto. */}
        <ButtonSolid title="Email our care team" variant="purple" onClick={handleEmailCareTeam} />
        <p className={styles.finePrint}>
          Want to reach us directly? Email <span className={styles.emailAddress}>{CARE_TEAM_EMAIL}</span> and we'll get
          you scheduled.
        </p>
      </div>
    </div>
  );
};

export default CareTeamHandoffInterstitial;
